import React, { useEffect, useRef, useState } from "react";
import { useDocumentTitle } from "../../hooks/customDocumentTitleHook";
import { BookCheck, Loader, LogOut, Printer, Trash2 } from "lucide-react";
import html2pdf from "html2pdf.js";
import { Config } from "../../Config";
import { useNavigate } from "react-router-dom";
import { getPractice, getSecureData, notify, setSecureData } from "../../helpers";
import { baaAgreementSign } from "../../services/workerServices";
import { getLocation } from "../../services/appointment";
import { useLogout } from "../../hooks/useLogout";

export default function BAAAgreementSign() {
  let account_id = getPractice("account_id");
  let user_id = getPractice("user_id");

  const effectiveDate = "May 01, 2024";
  const [mode, setMode] = useState("draw");
  const [typedSignature, setTypedSignature] = useState("");
  const [byName, setByName] = useState("");
  const [titleText, setTitleText] = useState("");
  const [isSaved, setIsSaved] = useState(false);
  const [sigError, setSigError] = useState("");
  const [byError, setByError] = useState("");
  const [titleError, setTitleError] = useState("");
  const [allLocatinNames, setAllLocatinNames] = useState("");
  const navigate = useNavigate();
  const portalLogout = useLogout();

  const canvasRef = useRef(null);
  const sigAreaRef = useRef(null);
  const byInputRef = useRef(null);
  const titleInputRef = useRef(null);
  const scrollRef = useRef(null);
  const sizingRef = useRef(null);
  const drawing = useRef(false);
  const lastPt = useRef({ x: 0, y: 0 });
  const hasInk = useRef(false);

  const scrollToEl = (el) => {
    if (!el) return;
    const container = scrollRef.current;
    if (container) {
      const elRect = el.getBoundingClientRect();
      const contRect = container.getBoundingClientRect();
      const top =
        container.scrollTop + (elRect.top - contRect.top) - 80;
      container.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
    } else {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    setTimeout(() => el.querySelector("input,textarea,canvas")?.focus?.(), 120);
  };

  const resizeCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const width = Math.min(420, (sizingRef.current?.clientWidth || 420) - 0);
    const height = 140;
    canvas.style.width = width + "px";
    canvas.style.height = height + "px";
    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(height * dpr);
    const ctx = canvas.getContext("2d");
    ctx.setTransform(1,0,0,1,0,0);
    ctx.scale(dpr, dpr);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = "#111";
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2.2;

    if (mode === "type" && typedSignature) {
      drawTypedSignature();
    }
  };

  useEffect(() => {
    fetchLocations(account_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account_id]);

  const fetchLocations = async (accountId) => {
    try {
      const response = await getLocation(accountId);
      if (response) {
        const results = response?.data ?? [];
        const names = results?.map(item => item?.name).join(", ");
        setAllLocatinNames(names);
      }
    } catch (error) { }
  };

  useEffect(() => {
    resizeCanvas();
    const obs = new ResizeObserver(resizeCanvas);
    if (sizingRef.current) obs.observe(sizingRef.current);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  useEffect(() => {
    if (mode === "type") drawTypedSignature();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typedSignature]);

  const getCtx = () => canvasRef.current?.getContext("2d");

  const clearCanvas = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = getCtx();
    const { width, height } = c;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, width, height);
    hasInk.current = false;
    resizeCanvas();
  };

  const drawTypedSignature = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = getCtx();
    const cssW = parseFloat(c.style.width) || 560;
    const cssH = parseFloat(c.style.height) || 160;
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, cssW, cssH);

    ctx.fillStyle = "#111";
    ctx.font = "36px 'Segoe Script', 'Brush Script MT', 'Snell Roundhand', cursive";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    if (typedSignature?.trim()) {
      ctx.fillText(typedSignature.trim(), cssW / 2, cssH / 2);
      hasInk.current = true;
    } else {
      hasInk.current = false;
    }
  };

  // Pointer handlers (draw mode only)
  const toPoint = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    return { x, y };
  };
  const handleDown = (e) => {
    if (mode !== "draw") return;
    drawing.current = true;
    lastPt.current = toPoint(e);
  };
  const handleMove = (e) => {
    if (mode !== "draw" || !drawing.current) return;
    const ctx = getCtx();
    const p = toPoint(e);
    ctx.beginPath();
    ctx.moveTo(lastPt.current.x, lastPt.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPt.current = p;
    hasInk.current = true;
    e.preventDefault();
  };
  const handleUp = () => {
    if (mode !== "draw") return;
    drawing.current = false;
  };

  const validate = () => {
    let ok = true;
    let firstMissingEl = null;

    // Signature required
    if (!hasInk.current) {
      setSigError("Signature is required");
      ok = false;
      firstMissingEl = firstMissingEl || sigAreaRef.current;
    } else {
      setSigError("");
    }
    // By required
    if (!byName.trim()) {
      setByError("'By' is required");
      ok = false;
      firstMissingEl = firstMissingEl || byInputRef.current?.parentElement;
    } else {
      setByError("");
    }
    // Title required
    if (!titleText.trim()) {
      setTitleError("'Title' is required");
      ok = false;
      firstMissingEl = firstMissingEl || titleInputRef.current?.parentElement;
    } else {
      setTitleError("");
    }

    if (!ok && firstMissingEl) {
      requestAnimationFrame(() => scrollToEl(firstMissingEl));
    }
    return ok;
  };

  // Save
  const saveSignature = async () => {
    if (mode === "type") drawTypedSignature();
    if (!validate()) {
      notify("error", "Please complete all the required fields.", 5);
      return;
    }

    const el = scrollRef.current;
    const filename = `BAA_Agreement_${user_id || 'user'}_${new Date().toISOString().slice(0,10)}.pdf`;
    el.classList.add("print-mode");

    const opt = {
      margin: [15, 15, 20, 15],
      filename,
      image: { type: 'jpeg', quality: 0.7 },
      html2canvas: {
        scale: 1.3,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        removeContainer: true,
        windowWidth: el.scrollWidth
      },
      jsPDF: { unit: 'pt', format: 'a4', orientation: 'p' },
      pagebreak: { mode: ['css'] },
    };

    const blob = await html2pdf().set(opt).from(el).toPdf().get('pdf').then(pdf => pdf.output('blob'));
    const file = new File([blob], filename, { type: 'application/pdf' });

    const formData = new FormData();
    formData.append('account_id', account_id ?? '');
    formData.append('user_id', user_id ?? '');
    formData.append('baa_agreement_file', file, filename);

    setIsSaved(true);
    try {
      const response = await baaAgreementSign(formData);
      
      if (response?.detail) {
        notify("error", response?.detail, 5);
      } else { 
        if(response?.data?.status === 'success'){
          let prevPracticeInfo = {};
          try {
            const stored = await getSecureData("practiceInfo");
            if (stored) prevPracticeInfo = JSON.parse(stored);
          } catch (e) {}
          const updatedPracticeInfo = {
            ...prevPracticeInfo,
            baa_agreement: "default.pdf",
          };
          await setSecureData("practiceInfo", JSON.stringify(updatedPracticeInfo));

          notify("success", response?.data?.message, 5);
          navigate("/", { replace: true });
        } else {
          notify("error", response?.data?.message, 5);
        }
      } 
    } catch (error) {
      if (error?.response && error?.response?.data && error?.response?.data?.detail) {
        notify("error", error?.response?.data?.detail, 5);
      }
    } finally { 
      el.classList.remove("print-mode");
      setIsSaved(false);
    }
  };

  function SectionTitle({ index, title, clas="" }) {
    return (
      <h2 className={`section-title ${clas}`}>
        <span className="idx">{index}</span>
        <span>{title}</span>
      </h2>
    );
  }

  function NumberedList({ items, alpha = false }) {
    return (
      <ol className={alpha ? 'alpha' : 'rom'}>
        {items.map((t, i) => (
          <li key={i} dangerouslySetInnerHTML={{ __html: t }}></li>
        ))}
      </ol>
    );
  }

  const sigCanvasClass = `sig-canvas ${sigError ? 'error' : ''}`;
  const byClass = `input ${byError ? 'input-error' : ''}`;
  const titleClass = `input ${titleError ? 'input-error' : ''}`;

  useDocumentTitle("BAA Agreement");

  return (
    <div className="docpro_signout_wrap small_page_wrap relative">
      <button
        className="docpro_bordered_btn docpro_fw_600 docpro_primary_color docpro_filed_btn docpro_hide_print"
        style={{ padding: "10px 26px", position: "absolute", top: 16, right: 30, zIndex: 10 }}
        onClick={saveSignature}
        disabled={isSaved}
      >
        <BookCheck height={18} width={18} />
        <span>Finish</span>
        {isSaved && (
          <span className="docpro_loading">
            <Loader width={20} height={20} />
          </span>
        )}
      </button>

      <div ref={scrollRef} className="docpro_signout relative docpro_full_height docpro_signout_previsit_print_document docpro_agreement_plan custom-scroll">
        {/* Header */}
        <header className="header">
          <img src={`${Config.appUrl}/images/logo_2.svg`} alt="Logo" />
          <h1>HIPAA Business Associate Agreement</h1>
          <div className="border"></div>
        </header>

        {/* Preamble */}
        <p>
          This HIPAA Business Associate Agreement ("Agreement") is entered into by and between
          <strong> DocPro Health</strong>, ("Business Associate") with its principal place of business located at
          <strong> 101-15 Metropolitan Avenue, Queens NY 11375</strong>, and
          <strong> <u>&nbsp;{getPractice("account_name")}&nbsp;</u></strong> ("Covered Entity") with its principal place of business located at
          <strong> <u>&nbsp;{allLocatinNames}&nbsp;</u></strong>.
        </p>
        <p>
          WHEREAS, Business Associate, in connection with its services, may maintain, transmit, create or receive data for or from Covered
          Entity that constitutes Protected Health Information ("PHI");
        </p>
        <p>
          WHEREAS, Covered Entity is or may be subject to the requirements of the Federal Health Insurance Portability and Accountability
          Act of 1996 ("HIPAA"), the Health Information Technology for Economic and Clinical Health Act ("HITECH"), and related
          regulations;
        </p>
        <p>
          WHEREAS, with respect to the foregoing, Business Associate is or may be subject to the requirements of HIPAA, HITECH and
          related regulations.
        </p>
        <p className="mb">NOW, THEREFORE, in consideration of the mutual promises and covenants contained herein, the Parties hereby agree as follows:</p>

        {/* 1. Definitions */}
        <SectionTitle index="1." title="Definitions." />
        <p>
          <strong>a. General.</strong> The following terms used in this Agreement shall have the same meaning as those terms in the HIPAA Rules: Breach,
          Data Aggregation, Designated Record Set, Disclosure, Electronic Protected Health Information, Health Care Operations,
          Individual, Minimum Necessary, Notice of Privacy Practices, Protected Health Information, Required by Law, Secretary, Security
          Incident, Subcontractor, Unsecured Protected Health Information, and Use.
        </p>
        <p className="boldline"><strong>b. Specific.</strong></p>
        <NumberedList
          items={[
            '<strong>Business Associate.</strong> “Business Associate” shall generally have the same meaning as the term “business associate” at 45 CFR 160.103, and in reference to the party to this Agreement, shall mean DocPro Health.',
            `<strong>Covered Entity.</strong> “Covered Entity” shall generally have the same meaning as the term “covered entity” at 45 CFR 160.103, and in reference to the party to this Agreement, shall mean <strong> <u>&nbsp;${getPractice("account_name")}&nbsp;</u></strong>.`,
            '<strong>Electronic Health Record.</strong> “Electronic Health Record” shall have the same meaning as the term “electronic health record” in the HITECH Act, Section 13400.',
            '<strong>HIPAA.</strong> “HIPAA” collectively refers to the HIPAA Statute, including the Privacy, Security, Breach Notification, and Enforcement Rules at 45 CFR Part 160 and Part 164, the HITECH Act, and any associated Regulations, as such may be amended from time to time.',
          ]}
        />

        {/* 2. Obligations and Activities of Business Associate */}
        <SectionTitle index="2." title="Obligations and Activities of Business Associate." />
        <NumberedList
          alpha
          items={[
            'Business Associate agrees to not use or disclose PHI other than as permitted or required by the Agreement or as required by law.',
            'Business Associate agrees to use appropriate safeguards, and comply with Subpart C of 45 CFR Part 164 with respect to Electronic PHI, to prevent use or disclosure of PHI other than as provided for by the Agreement.',
            'Business Associate agrees to report to Covered Entity any use or disclosure of PHI not provided for by the Agreement of which it becomes aware, including breaches of unsecured PHI as required at 45 CFR 164.410, and any security incident of which it becomes aware.<span class="show-br"><br/><br/><br/></span>',
            'In accordance with 45 CFR 164.502(e)(1) and 164.308(b)(2), if applicable, Business Associate agrees to ensure that any subcontractors that create, receive, maintain, or transmit PHI on behalf of the Business Associate agree to the same restrictions, conditions, and requirements that apply to the Business Associate with respect to such information.',
            'In accordance with 45 CFR 164.524, Business Associate agrees to make available PHI in a designated record set to the Covered Entity within twenty (20) days of a request by Covered Entity for access to PHI about an individual. In the event that any individual requests access to PHI directly from Business Associate, Business Associate shall forward such request to Covered Entity within twenty (20) days of receiving such request.',
            'In accordance with 45 CFR 164.526, Business Associate agrees to make any amendment(s) to PHI in a designated record within twenty (20) days of a request by Covered Entity. Business Associate shall provide such information to Covered Entity for amendment and incorporate any amendments in the PHI as required by 45 CFR 164.526. In the event a request for an amendment is delivered directly to Business Associate, Business Associate shall forward such request to Covered Entity within twenty (20) days of receiving such request.',
            'Except for disclosures of PHI by Business Associate that are excluded from the accounting obligation as set forth in 45 CFR 164.528 or regulations issued pursuant to HITECH, Business Associate shall record for each disclosure the information required to be recorded by Covered Entities pursuant to 45 CFR 164.528. Within twenty (20) days of notice by Covered Entity to Business Associate that it has received a request for an account of disclosures of PHI, Business Associate shall make available to Covered Entity, or if requested by Covered Entity, to the individual, the information required to be maintained pursuant to this Agreement. In the event the request for an accounting is delivered directly to Business Associate, Business Associate shall forward such request to Covered Entity within twenty (20) days of receiving such request.',
            "To the extent the Business Associate is to carry out one or more of Covered Entity's obligation(s) under Subpart E of 45 CFR Part 164, Business Associate agrees to comply with the requirements of Subpart E that apply to the Covered Entity in the performance of such obligation(s).",
            'Business Associate agrees to make its internal practices, books, and records relating to the use and disclosure of PHI available to the Secretary for purposes of determining compliance with HIPAA.',
          ]}
        />

        {/* 3. Permitted Uses and Disclosures by Business Associate */}
        <SectionTitle index="3." title="Permitted Uses and Disclosures by Business Associate" />
        <NumberedList
          alpha
          items={[
            'Business Associate may use or disclose PHI for the following purposes: As necessary to perform the services as agreed to between the Parties, notwithstanding the restrictions on such uses and disclosures as set forth in HIPAA and this Agreement.',
            'Business Associate may only de-identify PHI if permitted by Covered Entity and in any event may only de-identify PHI in accordance with 45 CFR 164.514(a)-(c).',
            'Business Associate may use or disclose PHI as required by law or where Business Associate obtains reasonable assurances from the person to whom the information is disclosed that the information will remain confidential and used or further disclosed only as required by law or for the purposes for which it was disclosed to the person, and the person notifies Business Associate of any instances of which it is aware in which the confidentiality of the information has been breached.',
            'Business Associate may not use or disclose PHI in a manner that would violate Subpart E of 45 CFR Part 164 if done by Covered Entity except for the specific uses and disclosures set forth herein.',
          ]}
        />

        {/* 4. Permissible Requests by Covered Entity */}
        <SectionTitle index="4." title="Permissible Requests by Covered Entity" />
        <NumberedList
          alpha
          items={[
            'Except as otherwise permitted by this Agreement, Covered Entity shall not request Business Associate to use or disclose PHI in any manner that would not be permissible under Subpart E of 45 CFR Part 164 if done by Covered Entity.',
          ]}
        />

        <div className="show-br"><br/><br/><br/></div>
        {/* 5. Term and Termination */}
        <SectionTitle index="5." title="Term and Termination" clas="page-break" />
        <NumberedList
          alpha
          items={[
            `<strong>Term.</strong> The Term of this Agreement shall be effective as of ${effectiveDate}, and shall terminate on the date the business relationship, or any services agreements, between the Parties end or are terminated or on the date Covered Entity terminates for cause as authorized in paragraph (b) of this Section.`,
            '<strong>Termination for Cause.</strong> Business Associate authorizes termination of this Agreement by Covered Entity, if Covered Entity determines Business Associate has violated a material term of the Agreement and Business Associate has not cured the breach or ended the violation within fifteen (15) days written notice. If it is determined by Covered Entity that cure is not possible, Covered Entity may immediately terminate this Agreement. The termination of this Agreement shall automatically terminate the business relationship and any services agreements between the Parties.',
            '<strong>Obligations of Business Associate Upon Termination.</strong> Upon termination of this Agreement, Business Associate shall either return or destroy all PHI that Business Associate still maintains in any form. Business Associate shall not retain any copies of such PHI. In the event Business Associate determines that returning or destroying the PHI is infeasible, the terms of this Agreement shall survive termination with respect to such PHI and limit further uses and disclosures of such PHI for so long as Business Associate maintains such PHI. In addition, Business Associate shall continue to use appropriate safeguards and comply with Subpart C of 45 CFR Part 164 with respect to electronic PHI to prevent use or disclosure of the PHI for as long as business associate retains the PHI.',
            '<strong>Survival.</strong> The obligations of Business Associate under this Section shall survive the termination of this Agreement.',
          ]}
        />

        {/* 6. General Provisions */}
        <SectionTitle index="6." title="General Provisions." />
        <p>
          This agreement sets forth the entire understanding of the Parties. Any amendments must be in writing and signed by both Parties.
          This Agreement shall be construed under the laws of the State of <b><u>&nbsp;New York&nbsp;</u></b> without regard to conflict of law provisions. Any
          ambiguity in the terms of this Agreement shall be resolved to permit compliance with HIPAA. Any references in this Agreement to a
          section in HIPAA means the section as in effect or as may be amended. This Agreement may be modified or amended from time to
          time as is necessary for compliance with the requirements of HIPAA and other applicable law. Amendments must be made in writing
          and signed by the Parties. The failure of either Party to enforce any provision of this Agreement shall not be construed as a waiver or
          limitation of that Party's right to subsequently enforce and compel strict compliance with every provision of this Agreement. The terms
          of this Agreement are hereby incorporated into any service or business agreement that may be entered into between the Parties with
          the intent to form a business relationship. In the event of a conflict of terms between this Agreement and any such service or
          business agreement the terms of this Agreement shall prevail.
        </p>
        <p>
          IN WITNESS WHEREOF, I have hereunto set my hand to this HIPAA Business Associate
          Agreement as of the date set forth above.
        </p>

        {/* Signatures */}
        <section className="signatures">
          <div>
            <div className="sig-heading">
              Covered Entity
              <div className="sig-mode">
                <label><input type="radio" name="sigModeCE" checked={mode==='draw'} onChange={() => setMode('draw')} /> Draw</label>
                <label><input type="radio" name="sigModeCE" checked={mode==='type'} onChange={() => setMode('type')} /> Type</label>
              </div>
            </div>

            <div className="canvas-area">
              <div className="sig-actions" style={{ display: mode === 'type' ? 'none' : 'block' }}>
                <button type="button" onClick={clearCanvas} className="btn"><Trash2 height={16} width={16} color="red"/></button>
              </div>
              <canvas
                ref={canvasRef}
                className={sigCanvasClass}
                onMouseDown={handleDown}
                onMouseMove={handleMove}
                onMouseUp={handleUp}
                onMouseLeave={handleUp}
                onTouchStart={handleDown}
                onTouchMove={handleMove}
                onTouchEnd={handleUp}
                aria-label="Signature pad"
              />
              {sigError ? <div className="field-error" role="alert">{sigError}</div> : null}
            </div>

            <div className="typed-wrap" style={{ display: mode === 'type' ? 'block' : 'none' }}>
              <input
                type="text"
                className={`input ${sigError ? 'input-error' : ''}`}
                placeholder="Typed text will render as the signature"
                value={typedSignature}
                onChange={(e) => { setTypedSignature(e.target.value); setSigError(""); }}
              />
            </div>

            <div className="sig-meta-wrap">
              <label className="lbl">By: </label>
              <input ref={byInputRef} className={byClass} type="text" value={byName} onChange={(e)=>{ setByName(e.target.value); setByError(""); }} placeholder="Signer name" />
              {byError ? <div className="field-error" role="alert">{byError}</div> : null}
            </div>
            <div className="sig-meta-wrap">
              <label className="lbl">Title: </label>
              <input ref={titleInputRef} className={titleClass} type="text" value={titleText} onChange={(e)=>{ setTitleText(e.target.value); setTitleError(""); }} placeholder="Signer title" />
              {titleError ? <div className="field-error" role="alert">{titleError}</div> : null}
            </div>
          </div>

          <div>
            <div className="sig-heading">Business Associate</div>
            <div className="sig-block">
              <div>
                <div className="sig-canvas md">
                  <img src={`${Config.appUrl}/images/signature.png`} alt="Logo" />
                </div>
                <div className="sig-meta">By: <span style={{opacity:.7}}>Tanwir Hasan</span></div>
                <div className="sig-meta">Title: <span style={{opacity:.7}}>CEO, DocPro Health</span></div>
              </div>
            </div>
          </div>
        </section>

        {/* Inline, single-file CSS */}
        <style>{`
          .docpro_agreement_plan{height:100vh; overflow-y:auto;}
          .header{text-align:center; margin-bottom:18px;}
          .header .border{border-top:2px solid #000; border-bottom:2px solid #000; padding: 1px;}
          .header img{height: 40px;margin-bottom:20px;}
          .header h1{font-size:20px; letter-spacing:.02em; text-transform:uppercase; font-weight:600; margin:0;}

          p{ margin:0 0 12px;}
          p.mb{ margin-bottom:18px;}
          .boldline{ font-weight:600; margin:6px 0 8px;}
          .spaced p{ margin:0 0 10px;}

          .section-title{ font-size:1.1rem; font-weight:600; margin:18px 0 10px; display:flex; gap:6px; align-items:baseline;}
          .section-title .idx{ margin-right:4px;}

          ol.rom{ counter-reset:item; list-style:lower-roman; margin:0 0 14px 22px; padding:0;}
          ol.alpha{ list-style:lower-alpha; margin:0 0 14px 22px; padding:0;}
          ol.rom li, ol.alpha li{ margin:6px 0;}
          ol li::marker, ul li::marker {font-weight:bold;}

          .signatures{display:grid;grid-template-columns:1fr 1fr;gap:28px;margin-top:22px;}
          @media (max-width:720px){.signatures{grid-template-columns:1fr;} }
          .sig-heading{font-weight:600;margin-bottom:10px; display: flex; align-items:center; justify-content: space-between;}
          .sig-grid{display:grid;grid-template-columns:1fr 260px;gap:16px;}
          @media (max-width:720px){.sig-grid{grid-template-columns:1fr;} }
          .sig-mode{display: flex; align-items: center;}
          .sig-mode label{margin-right:10px; display: flex; align-items: center; margin-bottom: 0;}
          .sig-mode label input{margin-right:5px;}
          .canvas-area{position: relative;}
          .sig-actions{position: absolute; top:1px; right: 1px;}
          .sig-actions .btn{padding:4px 8px;border:0;background:#fff;border-radius:9px;cursor:pointer;}
          .sig-canvas{width:100%!important; height:140px !important;display:block;background:#fff;border-radius:6px;border:1px solid #e5e7eb;touch-action:none; margin-bottom: 6px;}
          .sig-canvas.md{display:flex; align-items: center; justify-content: center;}
          .sig-canvas.md img{max-width: 80%; object-fit: cover;}
          .typed-wrap{margin:3px 0 10px;}
          .lbl{display:block;font-size:14px;color:#374151;margin-bottom:0;}
          .input{display:block;width:100%;padding:3px 7px;border:0; border-bottom:1px solid #d1d5db;outline:0;border-radius:0;}
          .sig-meta-wrap {display: flex; gap: 5px; align-items:end; margin-bottom: 5px; position: relative;}
          .sig-block{ display:flex; flex-direction:column; gap:18px;}
          .sig-meta{ margin-top:4px;}
          .sig-canvas.error{outline:1px solid #ef4444;}
          .input-error{border-color:#ef4444;}
          .field-error{color:#b91c1c;font-size:13px;position: absolute; bottom:1px; right: 1px;}

          .actions{ margin-top:28px; display:flex; align-items:end; justify-content:end; gap:10px;}
          .actions button{ border:1px solid #d1d5db; background:#fff; padding:10px 30px; border-radius:8px; cursor:pointer;}
          .actions button:active{ transform:translateY(1px);}
          .show-br { display: none;}
          @media print{.docpro_agreement_plan{height:auto !important; overflow-y:visible;}.sig-actions,.field-error,.typed-wrap,.sig-mode,.docpro_hide_print{display:none !important;}}
          .docpro_agreement_plan.print-mode{height:auto !important; overflow-y:visible;}
          .docpro_agreement_plan.print-mode p, .docpro_agreement_plan.print-mode li{line-height: 1.42 !important;}
          .docpro_agreement_plan.print-mode .show-br{display: block;}
          .print-mode .sig-actions,.print-mode .field-error,.print-mode .typed-wrap,.print-mode .sig-mode,.print-mode .docpro_hide_print {display: none !important;}
        `}</style>
      </div>

      <div className="docpro_invoice_btns docpro_hide_print">
        <button
          type="button"
          className="docpro_invoice_btn docpro_color3"
          onClick={portalLogout}
        >
          <span className="docpro_btn_icon">
            <LogOut height={24} width={24} />
          </span>
          <span className="docpro_btn_text">Logout</span>
        </button>
        <button
          type="button"
          className="docpro_invoice_btn docpro_color1"
          onClick={() => {window.print();}}
        >
          <span className="docpro_btn_icon">
            <Printer height={24} width={24} />
          </span>
          <span className="docpro_btn_text">Print</span>
        </button>
        {/* <button
          type="button"
          className="docpro_invoice_btn docpro_color2"
          onClick={saveSignature}
          disabled={isSaved}
        >
          <span className="docpro_btn_icon">
            <Save height={24} width={24} />
            {isSaved && (
              <span className="docpro_loading" style={{ background: '#FFFFFF' }}>
                <Loader width={20} height={20} />
              </span>
            )}
          </span>
          <span className="docpro_btn_text">Save</span>
        </button> */}
      </div>
    </div>
  );
}
